import React, { useState, useEffect } from 'react';
import { LeadCompetitionData, LeadAthlete } from '../utils/leadCompetition';
import { loadLeadData } from '../firebase/leadService';
import { useCompetition } from '../context/CompetitionContext';

// ─── Sub-components ───────────────────────────────────────────────────────────

const ScoreBox: React.FC = () => (
  <div style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
    <div style={{ border: '1.5px solid #333', width: 52, height: 26, borderRadius: 3 }} />
    <label style={{ fontSize: 10, display: 'flex', alignItems: 'center', gap: 2 }}>
      <input type="checkbox" style={{ width: 12, height: 12 }} /> TOP
    </label>
    <label style={{ fontSize: 10, display: 'flex', alignItems: 'center', gap: 2 }}>
      <input type="checkbox" style={{ width: 12, height: 12 }} /> DNF
    </label>
  </div>
);

const GroupTable: React.FC<{ label: string; athletes: LeadAthlete[] }> = ({ label, athletes }) => (
  <div style={{ marginBottom: 28, pageBreakInside: 'avoid' }}>
    <div style={{
      background: '#1e3a5f', color: 'white', padding: '6px 12px',
      borderRadius: '6px 6px 0 0', display: 'flex', justifyContent: 'space-between', alignItems: 'center',
    }}>
      <span style={{ fontWeight: 700, fontSize: 14 }}>{label}</span>
      <span style={{ fontSize: 12, opacity: 0.8 }}>{athletes.length} athletes</span>
    </div>
    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
      <thead>
        <tr style={{ background: '#f0f4f8', borderBottom: '2px solid #1e3a5f' }}>
          <th style={th}>#</th>
          <th style={{ ...th, textAlign: 'left', width: '35%' }}>Athlete</th>
          <th style={th}>Class</th>
          <th style={th}>Style</th>
          <th style={{ ...th, width: '22%' }}>Route 1 <span style={{ fontWeight: 400, fontSize: 10 }}>(holds / +)</span></th>
          <th style={{ ...th, width: '22%' }}>Route 2 <span style={{ fontWeight: 400, fontSize: 10 }}>(holds / +)</span></th>
          <th style={{ ...th, width: '10%' }}>Notes</th>
        </tr>
      </thead>
      <tbody>
        {athletes.map((a, i) => (
          <tr key={a.id} style={{ borderBottom: '1px solid #ddd', background: i % 2 === 0 ? '#fff' : '#f9fafb' }}>
            <td style={{ ...td, textAlign: 'center', color: '#888', fontSize: 11 }}>{i + 1}</td>
            <td style={{ ...td, fontWeight: 600 }}>{a.firstName} {a.lastName}</td>
            <td style={{ ...td, textAlign: 'center', fontWeight: 700, color: '#1e3a5f' }}>{a.classId}</td>
            <td style={{ ...td, textAlign: 'center', fontSize: 11, color: a.isLead ? '#1d4ed8' : '#6b7280' }}>
              {a.isLead ? 'Lead' : 'Top rope'}
            </td>
            <td style={{ ...td, textAlign: 'center' }}><ScoreBox /></td>
            <td style={{ ...td, textAlign: 'center' }}><ScoreBox /></td>
            <td style={{ ...td }}></td>
          </tr>
        ))}
      </tbody>
    </table>
    <div style={{ borderTop: '1px solid #ddd', padding: '6px 12px', display: 'flex', gap: 40, fontSize: 11, color: '#555', background: '#fafafa' }}>
      <span>Jury signature: _______________________</span>
      <span>Time completed: ___________</span>
    </div>
  </div>
);

const th: React.CSSProperties = {
  padding: '5px 8px', textAlign: 'center', fontSize: 11,
  fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.04em', color: '#1e3a5f',
};
const td: React.CSSProperties = { padding: '5px 8px' };

// ─── LeadJuryPrintPage ────────────────────────────────────────────────────────

const LeadJuryPrintPage: React.FC = () => {
  const { competitionId, metadata } = useCompetition();
  const [leadData, setLeadData] = useState<LeadCompetitionData | null>(null);
  const [loading,  setLoading]  = useState(true);

  useEffect(() => {
    loadLeadData(competitionId)
      .then(d => { setLeadData(d); setLoading(false); })
      .catch(() => setLoading(false));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [competitionId]);

  // Build sections: explicit groups + ungrouped athletes split by (classId, gender)
  const allGroups   = leadData?.setup.groups  ?? [];
  const allAthletes = leadData?.athletes       ?? [];
  const allClasses  = leadData?.setup.classes  ?? [];

  type Section = { key: string; label: string; athletes: LeadAthlete[] };
  const sections: Section[] = [];

  allGroups.forEach(g => {
    const grouped = allAthletes
      .filter(a => a.groupId === g.id)
      .sort((a, b) => `${a.classId}${a.lastName}`.localeCompare(`${b.classId}${b.lastName}`));
    if (grouped.length === 0) return;
    const label = g.gender ? `${g.name} ${g.gender === 'men' ? 'Men' : 'Women'}` : g.name;
    sections.push({ key: g.id, label, athletes: grouped });
  });

  const ungrouped = allAthletes.filter(a => !a.groupId);
  Array.from(new Set(ungrouped.map(a => a.classId))).forEach(classId => {
    (['male', 'female'] as const).forEach(gender => {
      const sub = ungrouped
        .filter(a => a.classId === classId && a.gender === gender)
        .sort((a, b) => a.lastName.localeCompare(b.lastName));
      if (sub.length === 0) return;
      const className = allClasses.find(c => c.id === classId)?.name ?? classId;
      sections.push({ key: `${classId}-${gender}`, label: `${className} ${gender === 'male' ? 'Men' : 'Women'}`, athletes: sub });
    });
  });

  const noAthletes = allAthletes.length === 0;

  return (
    <div style={{ fontFamily: 'Arial, sans-serif', color: '#111', minHeight: '100vh', background: '#fff' }}>
      <style>{`
        @media print {
          nav, footer, .no-print { display: none !important; }
          body { margin: 0; }
          @page { margin: 12mm 10mm; size: A4; }
        }
      `}</style>

      {/* Screen-only controls */}
      <div className="no-print" style={{ padding: '16px 24px', background: '#1e3a5f', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <span style={{ color: 'white', fontWeight: 700, fontSize: 16 }}>
          {metadata?.name ?? competitionId} — Lead & Top-rope Jury Print Sheet
        </span>
        <button
          onClick={() => window.print()}
          style={{ background: 'white', color: '#1e3a5f', border: 'none', padding: '8px 20px', borderRadius: 6, fontWeight: 700, cursor: 'pointer', fontSize: 14 }}
        >
          🖨 Print / Save as PDF
        </button>
      </div>

      <div style={{ padding: '16px 20px', maxWidth: 900, margin: '0 auto' }}>
        {/* Header */}
        <div style={{ textAlign: 'center', marginBottom: 20, borderBottom: '3px solid #1e3a5f', paddingBottom: 12 }}>
          <div style={{ fontSize: 20, fontWeight: 900, letterSpacing: '0.02em' }}>
            🧗 {metadata?.name ?? competitionId}
          </div>
          <div style={{ fontSize: 15, fontWeight: 700, marginTop: 4, color: '#1e3a5f' }}>
            Lead &amp; Top-rope — Jury Score Sheet
          </div>
          {(metadata?.date || metadata?.location) && (
            <div style={{ fontSize: 12, marginTop: 4, color: '#555' }}>
              {metadata?.date} {metadata?.location ? `· ${metadata.location}` : ''}
            </div>
          )}
          <div style={{ marginTop: 10, fontSize: 11, color: '#333', background: '#f0f4f8', padding: '6px 12px', borderRadius: 4, display: 'inline-block', textAlign: 'left' }}>
            <strong>Scoring:</strong> write the hold number reached (e.g. <em>25</em>).
            Add <strong>+</strong> if the athlete also gripped the plus hold (e.g. <em>25+</em>).
            Tick <strong>TOP</strong> if athlete reached the top. Tick <strong>DNF</strong> if they did not finish.
          </div>
        </div>

        {loading ? (
          <p style={{ textAlign: 'center', color: '#888' }}>Loading athlete data…</p>
        ) : noAthletes ? (
          <p style={{ textAlign: 'center', color: '#888' }}>No athletes added yet. Add athletes in the Lead admin panel.</p>
        ) : (
          sections.map(s => (
            <GroupTable key={s.key} label={s.label} athletes={s.athletes} />
          ))
        )}

        <div style={{ marginTop: 24, fontSize: 10, color: '#999', textAlign: 'center' }}>
          {metadata?.name ?? competitionId} · Generated {new Date().toLocaleDateString('en-GB')}
        </div>
      </div>
    </div>
  );
};

export default LeadJuryPrintPage;
